import { useState, useEffect } from "react";
import { CalendarX, Clock, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { WeeklySchedule } from "./WeeklySchedule";

const DEFAULT_SLOTS = ["09:00", "10:00", "11:00", "12:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

export const AvailabilityTab = () => {
  const [overrides, setOverrides] = useState([]);
  const [date, setDate] = useState("");
  const [closed, setClosed] = useState(false);
  const [slots, setSlots] = useState(DEFAULT_SLOTS);
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/availability").then((r) => setOverrides(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const o = overrides.find((x) => x.date === date);
    setClosed(o ? !!o.closed : false);
    setSlots(o && !o.closed && o.slots?.length ? o.slots : DEFAULT_SLOTS);
  }, [date, overrides]);

  const toggleSlot = (s) => setSlots((l) => (l.includes(s) ? l.filter((x) => x !== s) : [...l, s].sort()));

  const addCustom = () => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(custom)) { toast.error("Formato orario non valido (usa HH:MM, es. 14:30)"); return; }
    if (slots.includes(custom)) { toast.error("Fascia già presente"); return; }
    setSlots((l) => [...l, custom].sort());
    setCustom("");
  };

  const save = async () => {
    if (!date) { toast.error("Seleziona una data"); return; }
    setSaving(true);
    try {
      await api.put(`/availability/${date}`, { closed, slots });
      toast.success("Disponibilità salvata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/availability/${d}`);
      toast.success("Data ripristinata alle fasce standard");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const allSlots = [...new Set([...DEFAULT_SLOTS, ...slots])].sort();

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="lg:col-span-2">
        <WeeklySchedule />
      </div>
      <div className="bg-[#10141D] border border-[#222A3B] rounded-2xl p-6 space-y-6">
        <div>
          <Label htmlFor="avail-date" className="text-slate-300">Data da configurare</Label>
          <Input id="avail-date" data-testid="availability-date-input" type="date" value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-2 bg-[#08090C] border-[#222A3B] text-white [color-scheme:dark] focus:border-sky-500" />
        </div>
        <div className="flex items-center justify-between bg-[#08090C] border border-[#222A3B] rounded-xl p-4">
          <div>
            <p className="text-sm font-medium text-white">Giorno di chiusura</p>
            <p className="text-xs text-slate-500">Nessuna prenotazione possibile in questa data</p>
          </div>
          <Switch data-testid="availability-closed-switch" checked={closed} onCheckedChange={setClosed} />
        </div>
        {!closed && (
          <div>
            <Label className="text-slate-300 flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-400" /> Fasce orarie attive per questa data
            </Label>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {allSlots.map((s) => {
                const active = slots.includes(s);
                return (
                  <button key={s} type="button" data-testid={`availability-slot-${s.replace(":", "")}`}
                    onClick={() => toggleSlot(s)}
                    className={`py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                      active
                        ? "bg-sky-400 text-[#050B14] border-sky-400 shadow-[0_0_15px_rgba(56,189,248,0.25)]"
                        : "bg-[#08090C] border-[#222A3B] text-slate-500 hover:border-sky-500/40 hover:text-slate-300"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex gap-2">
              <Input data-testid="availability-custom-slot-input" placeholder="Nuova fascia, es. 14:30" value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
              <Button type="button" data-testid="availability-add-slot-btn" onClick={addCustom} variant="outline"
                className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10 bg-transparent shrink-0">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>
          </div>
        )}
        <Button data-testid="availability-save-btn" onClick={save} disabled={saving}
          className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold h-11">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salva disponibilità</>}
        </Button>
      </div>
      <div>
        <h3 className="font-heading font-semibold text-white mb-4">Date personalizzate</h3>
        {overrides.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center border border-dashed border-[#222A3B] rounded-2xl">
            Nessuna personalizzazione: tutte le date usano le fasce standard (09-12 / 15-19).
          </p>
        ) : (
          <div className="space-y-3">
            {overrides.map((o) => (
              <div key={o.date} data-testid={`availability-override-${o.date}`}
                className="flex items-center justify-between bg-[#10141D] border border-[#222A3B] rounded-xl p-4 hover:border-sky-500/30 transition-colors">
                <div>
                  <p className="text-white font-medium font-heading">{o.date}</p>
                  {o.closed ? (
                    <p className="text-xs text-red-400 flex items-center gap-1.5 mt-1">
                      <CalendarX className="w-3.5 h-3.5" /> Giorno di chiusura
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">{o.slots.join(" · ")}</p>
                  )}
                </div>
                <Button size="sm" variant="ghost" data-testid={`availability-delete-${o.date}`} onClick={() => remove(o.date)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
